import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Modal from '../common/Modal';
import api from '../../utils/api';
import {
  HomeIcon,
  CubeIcon,
  FolderIcon,
  UsersIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ShoppingBagIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  CogIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ClockIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

const AdminSidebar: React.FC<{ 
  variant?: 'desktop' | 'mobile'; 
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}> = ({ variant = 'desktop', onNavigate, collapsed = false, onToggleCollapse }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  const roleKey = user?.role === 'admin'
    ? 'admin.admin'
    : user?.role === 'customer'
      ? 'admin.customer'
      : 'admin.undefined';
  const roleLabel = t(roleKey, user?.role || '');

  // Fetch admin profile data when modal opens
  useEffect(() => {
    if (profileModalOpen && user?.role === 'admin') {
      fetchAdminProfile();
    }
  }, [profileModalOpen, user?.role]);

  const fetchAdminProfile = async () => {
    setLoadingProfile(true);
    try {
      const response = await api.get('/admin/users/profile');
      setProfileData(response.data);
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
      setProfileData(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  // Handle navigation click for mobile sidebar
  const handleNavigationClick = () => {
    if (variant === 'mobile' && onNavigate) {
      onNavigate();
    }
  };

  const navigation = [
    {
      name: t('admin.dashboard', 'Dashboard'),
      href: '/admin',
      icon: HomeIcon,
      description: t('admin.overview', 'Overview & Analytics')
    },
    {
      name: t('admin.products', 'Products'),
      href: '/admin/products',
      icon: CubeIcon,
      description: t('admin.productManagement', 'Manage Products')
    },
    {
      name: t('admin.categories', 'Categories'),
      href: '/admin/categories',
      icon: FolderIcon,
      description: t('admin.categoryManagement', 'Manage Categories')
    },
    {
      name: t('admin.orders', 'Orders'),
      href: '/admin/orders',
      icon: ShoppingBagIcon,
      description: t('admin.orderManagement', 'Order Management')
    },
    {
      name: t('admin.users', 'Users'),
      href: '/admin/users',
      icon: UsersIcon,
      description: t('admin.userManagement', 'User Management')
    },
    {
      name: t('admin.blogs', 'Blogs'),
      href: '/admin/blogs',
      icon: DocumentTextIcon,
      description: t('admin.blogManagement', 'Content Management')
    },
    {
      name: t('admin.support', 'Support'),
      href: '/admin/support',
      icon: ChatBubbleLeftRightIcon,
      description: t('admin.supportManagement', 'Customer Support')
    },
    {
      name: t('admin.settings', 'Settings'),
      href: '/admin/settings',
      icon: CogIcon,
      description: t('admin.settingsManagement', 'System Settings')
    },
  ];

  const sidebarWidth = collapsed ? 'md:w-20' : 'md:w-64';
  const wrapperClass = variant === 'desktop'
    ? `hidden md:flex ${sidebarWidth} md:flex-col md:fixed md:inset-y-0 ${isRTL ? 'md:right-0' : 'md:left-0'} md:top-0 z-40 transition-all duration-300`
    : 'flex w-72 max-w-[85vw] h-full flex-col z-50';

  return (
    <div className={wrapperClass}>
      <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200 shadow-xl">
        <div className="flex-1 flex flex-col pt-3 sm:pt-4 pb-3 overflow-y-auto">
          {/* Logo Section */}
          <div className={`flex items-center flex-shrink-0 px-3 sm:px-4 mb-4 sm:mb-6 ${collapsed ? 'justify-center' : ''}`}>
            {collapsed ? (
              <Link to="/" className="flex items-center justify-center group">
                <div className="h-10 w-10">
                  <img
                    src="/logo.png"
                    alt="RF Admin"
                    className="w-full h-full object-contain"
                  />
                </div>
              </Link>
            ) : (
              <Link to="/" className="flex items-center group">
                <div className="h-8 w-8 sm:h-10 sm:w-10">
                  <img
                    src="/logo.png"
                    alt="RF Admin"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className={`ml-2 sm:ml-2 ${isRTL ? 'mr-2 ml-0' : ''}`}>
                  <span className="text-sm sm:text-base font-bold text-gray-900">RF Box</span>
                  <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide">{t('admin.adminPanel', 'Admin Panel')}</div>
                </div>
              </Link>
            )}
          </div>

          {/* Collapse Toggle Button - Desktop Only */}
          {variant === 'desktop' && onToggleCollapse && (
            <div className={`px-3 mb-2 ${collapsed ? 'px-2' : ''}`}>
              <button
                onClick={onToggleCollapse}
                className={`w-full flex items-center justify-center ${collapsed ? 'px-2' : 'px-3'} py-2 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-200 group`}
                title={collapsed ? t('admin.expandSidebar', 'Expand Sidebar') : t('admin.collapseSidebar', 'Collapse Sidebar')}
              >
                {isRTL ? (
                  collapsed ? (
                    <ChevronLeftIcon className="h-5 w-5" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" />
                  )
                ) : (
                  collapsed ? (
                    <ChevronRightIcon className="h-5 w-5" />
                  ) : (
                    <ChevronLeftIcon className="h-5 w-5" />
                  )
                )}
                {!collapsed && (
                  <span className={`ml-2 text-xs font-medium ${isRTL ? 'mr-2 ml-0' : ''}`}>
                    {t('admin.collapse', 'Collapse')}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-2 sm:px-3 space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={handleNavigationClick}
                className={({ isActive }) =>
                  `group flex items-center ${collapsed ? 'justify-center px-2' : 'px-2 sm:px-3'} py-2.5 sm:py-3 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 relative ${
                    isActive
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                  }`
                }
                title={collapsed ? item.name : undefined}
              >
                <item.icon
                  className={`${collapsed ? '' : (isRTL ? 'ml-2 sm:ml-2.5 mr-0' : 'mr-2 sm:mr-2.5')} h-5 w-5 flex-shrink-0 transition-colors duration-200`}
                  aria-hidden="true"
                />
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs sm:text-sm truncate">{item.name}</div>
                    <div className={`text-xs hidden sm:block truncate ${
                      location.pathname === item.href 
                        ? 'text-emerald-100' 
                        : 'text-gray-500 group-hover:text-gray-600'
                    }`}>
                      {item.description}
                    </div>
                  </div>
                )}
                {/* Active indicator dot for collapsed state */}
                {collapsed && location.pathname === item.href && (
                  <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-l-full`} />
                )}
              </NavLink>
            ))}
          </nav>

          {/* Bottom Section */}
          <div className={`mt-auto ${collapsed ? 'px-2' : 'px-2 sm:px-3'} space-y-2 border-t border-gray-200 pt-3`}>
            {/* Profile Button */}
            <button
              onClick={() => setProfileModalOpen(true)}
              className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} px-2 sm:px-3 py-2.5 rounded-xl bg-gradient-to-r from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100 border border-emerald-200 transition-all duration-200 group`}
              title={collapsed ? (user?.firstName + ' ' + user?.lastName) : undefined}
            >
              <div className="h-8 w-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                {user?.firstName?.[0] && user?.lastName?.[0] ? (
                  <span className="text-sm font-bold text-white">
                    {user.firstName[0]}{user.lastName[0]}
                  </span>
                ) : (
                  <UserCircleIcon className="h-5 w-5 text-white" />
                )}
              </div>
              {!collapsed && (
                <div className={`ml-3 ${isRTL ? 'mr-3 ml-0' : ''} flex-1 min-w-0 text-left ${isRTL ? 'text-right' : ''}`}>
                  <p className="text-xs font-semibold text-gray-900 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-emerald-600 capitalize truncate">{roleLabel}</p>
                </div>
              )}
            </button>

            {/* Back to Site - Hidden when collapsed */}
            {!collapsed && (
              <Link
                to="/"
                onClick={handleNavigationClick}
                className="w-full inline-flex items-center justify-center px-2 sm:px-3 py-2 text-xs font-medium text-emerald-600 bg-white hover:bg-emerald-50 rounded-xl transition-colors duration-200 border border-emerald-200 hover:border-emerald-300"
              >
                <ArrowRightOnRectangleIcon className={`h-4 w-4 ${isRTL ? 'ml-1.5 mr-0' : 'mr-1.5'} ${isRTL ? 'rotate-0' : 'rotate-180'}`} />
                <span>{t('admin.backToHome', 'Back to Home')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Profile Modal */}
      <Modal 
        isOpen={profileModalOpen} 
        onClose={() => setProfileModalOpen(false)} 
        title={t('admin.profile', 'Profile Information')}
        size="lg"
      >
        {loadingProfile ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Profile Header */}
            <div className="text-center pb-6 border-b border-gray-200">
              <div className="mx-auto h-24 w-24 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow-xl mb-4 ring-4 ring-emerald-100">
                {user?.firstName?.[0] && user?.lastName?.[0] ? (
                  <span className="text-3xl font-bold text-white">
                    {user.firstName[0]}{user.lastName[0]}
                  </span>
                ) : (
                  <UserCircleIcon className="h-14 w-14 text-white" />
                )}
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {profileData?.firstName || user?.firstName} {profileData?.lastName || user?.lastName}
              </h3>
              <p className="text-sm text-emerald-600 font-semibold capitalize mt-1.5">
                {profileData?.roles?.[0] || roleLabel}
              </p>
            </div>

            {/* Profile Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-200/50">
                <div className="h-12 w-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                  <EnvelopeIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-blue-700 mb-1">{t('admin.email', 'Email')}</p>
                  <p className="text-sm font-semibold text-gray-900 truncate">{profileData?.email || user?.email}</p>
                  {profileData?.emailConfirmed && (
                    <span className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600">
                      <ShieldCheckIcon className="h-3 w-3" />
                      {t('admin.emailVerified', 'Verified')}
                    </span>
                  )}
                </div>
              </div>

              {/* Phone */}
              {profileData?.phoneNumber && (
                <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl border border-purple-200/50">
                  <div className="h-12 w-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                    <DevicePhoneMobileIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-purple-700 mb-1">{t('admin.phone', 'Phone')}</p>
                    <p className="text-sm font-semibold text-gray-900">{profileData.phoneNumber}</p>
                  </div>
                </div>
              )}

              {/* Member Since */}
              {profileData?.createdAtUtc && (
                <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200/50">
                  <div className="h-12 w-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                    <CalendarIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-emerald-700 mb-1">{t('admin.memberSince', 'Member Since')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {new Date(profileData.createdAtUtc).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              )}

              {/* Last Login */}
              {profileData?.lastLoginAtUtc && (
                <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl border border-amber-200/50">
                  <div className="h-12 w-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                    <ClockIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-amber-700 mb-1">{t('admin.lastActivity', 'Last Activity')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {new Date(profileData.lastLoginAtUtc).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              )}

              {/* Active Sessions */}
              {profileData?.activeSessionsCount !== undefined && (
                <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-xl border border-indigo-200/50">
                  <div className="h-12 w-12 bg-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                    <DevicePhoneMobileIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-indigo-700 mb-1">{t('admin.activeSessions', 'Active Sessions')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {profileData.activeSessionsCount} {t('admin.of', 'of')} {profileData.totalSessionsCount || 0}
                    </p>
                  </div>
                </div>
              )}

              {/* Two Factor Authentication */}
              {profileData?.twoFactorEnabled !== undefined && (
                <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl border border-green-200/50">
                  <div className="h-12 w-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                    <ShieldCheckIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-green-700 mb-1">{t('admin.twoFactor', 'Two-Factor Auth')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {profileData.twoFactorEnabled ? t('admin.enabled', 'Enabled') : t('admin.disabled', 'Disabled')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setProfileModalOpen(false);
                }}
                className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                {t('admin.close', 'Close')}
              </button>
              <button
                onClick={() => {
                  setProfileModalOpen(false);
                  logout();
                }}
                className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium transition-colors duration-200 border border-red-200 hover:border-red-300"
                title={t('admin.logout', 'Logout')}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminSidebar;
